// src/App.tsx
import React, { useState } from "react";
import {
  Layout,
  Card,
  Button,
  Typography,
  Progress,
  message,
  Result,
  ConfigProvider,
  Space,
  Popconfirm,
} from "antd";
import {
  RocketTwoTone,
  CheckCircleFilled,
  RightOutlined,
  LeftOutlined,
  ReloadOutlined,
  BulbOutlined,
  TrophyOutlined,
} from "@ant-design/icons";
import { questions } from "./questions";
import "./App.css";

const { Header, Content, Footer } = Layout;
const { Title, Text, Paragraph } = Typography;

// --- 1. HÀM XÁO TRỘN (Fisher-Yates Shuffle) ---
const shuffleArray = (array: any[]) => {
  const newArray = [...array];
  for (let i = newArray.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [newArray[i], newArray[j]] = [newArray[j], newArray[i]];
  }
  return newArray;
};

const App: React.FC = () => {
  // --- 2. STATE LƯU CÂU HỎI ĐÃ XÁO TRỘN ---
  // Khởi tạo state với danh sách câu hỏi đã được xáo trộn ngay từ đầu
  const [questionsData, setQuestionsData] = useState(() =>
    shuffleArray(questions)
  );

  const [isStarted, setIsStarted] = useState<boolean>(false);
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState<number>(0);
  const [userAnswers, setUserAnswers] = useState<Record<number, string>>({});
  const [isFinished, setIsFinished] = useState<boolean>(false);
  const [score, setScore] = useState<number>(0);

  const handleOptionSelect = (questionId: number, key: string) => {
    setUserAnswers({
      ...userAnswers,
      [questionId]: key,
    });
  };

  const handlePrevious = () => {
    if (currentQuestionIndex > 0) {
      setCurrentQuestionIndex(currentQuestionIndex - 1);
    }
  };

  const handleNext = () => {
    // Sử dụng questionsData thay vì questions
    if (!userAnswers[questionsData[currentQuestionIndex].id]) {
      message.warning("Hãy chọn một đáp án để tiếp tục!");
      return;
    }

    if (currentQuestionIndex < questionsData.length - 1) {
      setCurrentQuestionIndex(currentQuestionIndex + 1);
    } else {
      finishTest();
    }
  };

  const finishTest = () => {
    let correctCount = 0;
    // Duyệt qua questionsData để tính điểm
    questionsData.forEach((q) => {
      const userAnswer = userAnswers[q.id];
      const correctOption = q.options.find((opt: any) => opt.isCorrect);
      if (correctOption && userAnswer === correctOption.key) {
        correctCount++;
      }
    });
    const calculatedIQ =
      80 + Math.round((correctCount / questionsData.length) * 60);
    setScore(calculatedIQ);
    setIsFinished(true);
  };

  const resetTest = () => {
    setIsFinished(false);
    setIsStarted(false);
    setCurrentQuestionIndex(0);
    setUserAnswers({});
    setScore(0);
    // --- 3. XÁO TRỘN LẠI KHI RESET ---
    // Khi người dùng làm lại bài, tạo ra một thứ tự mới hoàn toàn
    setQuestionsData(shuffleArray(questions));
  };

  // Lấy câu hỏi hiện tại từ danh sách đã xáo trộn
  const currentQuestion = questionsData[currentQuestionIndex];

  const progressPercent = Math.round(
    ((currentQuestionIndex + 1) / questionsData.length) * 100
  );

  // Màn hình chào mừng
  const WelcomeScreen = () => (
    <Card
      className="iq-card fade-in"
      style={{ textAlign: "center", padding: "40px 20px" }}
    >
      <Space direction="vertical" size="large">
        <RocketTwoTone twoToneColor="#1890ff" style={{ fontSize: "64px" }} />
        <div>
          <Title level={2} style={{ marginBottom: 0, color: "#1e3c72" }}>
            Kiểm tra chỉ số IQ
          </Title>
          <Text type="secondary" style={{ fontSize: "16px" }}>
            {/* Sử dụng questionsData.length */}
            Khám phá tiềm năng trí tuệ của bạn qua {questionsData.length} câu
            hỏi thú vị.
          </Text>
        </div>

        <div
          style={{
            textAlign: "left",
            background: "#f7f9fc",
            padding: "20px",
            borderRadius: "12px",
            margin: "20px 0",
          }}
        >
          <Space direction="vertical">
            <Text strong>
              <BulbOutlined /> Quy tắc:
            </Text>
            <Text>1. Không được sử dụng công cụ hỗ trợ.</Text>
            <Text>2. Mỗi câu hỏi chỉ có một đáp án đúng.</Text>
            <Text>3. Có thể quay lại để sửa đáp án trước khi nộp bài.</Text>
          </Space>
        </div>

        <Button
          type="primary"
          size="large"
          shape="round"
          onClick={() => setIsStarted(true)}
          style={{ height: "50px", padding: "0 40px", fontSize: "18px" }}
        >
          Bắt đầu ngay <RightOutlined />
        </Button>
      </Space>
    </Card>
  );

  // Màn hình kết quả
  const ResultScreen = () => (
    <Card className="iq-card fade-in">
      <Result
        icon={<TrophyOutlined style={{ color: "#faad14" }} />}
        status="success"
        title={<Title level={2}>Chúc mừng bạn đã hoàn thành!</Title>}
        subTitle={
          <div style={{ textAlign: "center", marginTop: 20 }}>
            <Text style={{ fontSize: 18 }}>Chỉ số IQ ước tính của bạn là</Text>
            <div
              style={{
                fontSize: "80px",
                fontWeight: 800,
                color: "#1890ff",
                lineHeight: 1,
                margin: "20px 0",
                textShadow: "0 4px 10px rgba(24,144,255,0.2)",
              }}
            >
              {score}
            </div>
            <Paragraph
              type="secondary"
              style={{ maxWidth: 400, margin: "0 auto" }}
            >
              Kết quả này dựa trên số câu trả lời đúng của bạn so với bộ dữ liệu
              mẫu. Hãy tiếp tục rèn luyện mỗi ngày!
            </Paragraph>
          </div>
        }
        extra={[
          <Button
            type="primary"
            size="large"
            key="console"
            onClick={resetTest}
            shape="round"
          >
            Làm bài test mới
          </Button>,
        ]}
      />
    </Card>
  );

  return (
    <ConfigProvider
      theme={{
        token: {
          fontFamily: "'Work Sans', sans-serif",
          colorPrimary: "#1890ff",
          borderRadius: 8,
        },
      }}
    >
      <Layout className="app-layout">
        <Header className="app-header">
          <Space align="center">
            <div
              style={{
                width: 32,
                height: 32,
                background: "#1890ff",
                borderRadius: 8,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                color: "white",
                fontWeight: "bold",
              }}
            >
              IQ
            </div>
            <Title level={4} className="brand-title" style={{ margin: 0 }}>
              IQ Test Pro
            </Title>
          </Space>
        </Header>

        <Content
          style={{
            padding: "20px",
            display: "flex",
            justifyContent: "center",
            alignItems: "flex-start",
            marginTop: "20px",
          }}
        >
          <div style={{ maxWidth: "650px", width: "100%" }}>
            {!isStarted ? (
              <WelcomeScreen />
            ) : !isFinished ? (
              <Card
                className="iq-card fade-in"
                title={
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: "8px",
                    }}
                  >
                    <Text strong style={{ fontSize: 16 }}>
                      Câu hỏi {currentQuestionIndex + 1}
                    </Text>
                    <Text type="secondary" style={{ fontSize: 14 }}>
                      / {questionsData.length}
                    </Text>
                  </div>
                }
                extra={
                  <Popconfirm
                    title="Bạn có chắc muốn làm lại từ đầu?"
                    description="Tiến độ hiện tại sẽ bị mất và bộ câu hỏi sẽ được xáo trộn lại."
                    onConfirm={resetTest}
                    okText="Đồng ý"
                    cancelText="Hủy"
                  >
                    <Button type="text" danger icon={<ReloadOutlined />}>
                      Bắt đầu lại
                    </Button>
                  </Popconfirm>
                }
              >
                <Progress
                  percent={progressPercent}
                  showInfo={false}
                  strokeColor={{ "0%": "#108ee9", "100%": "#87d068" }}
                  size="small"
                  style={{ marginBottom: 30 }}
                />

                <Title level={4} style={{ marginBottom: 24, fontWeight: 600 }}>
                  {currentQuestion.questionText}
                </Title>

                {/* Sửa lại truy cập thuộc tính image nếu kiểu dữ liệu cho phép, 
                    hoặc cast kiểu any nếu TypeScript báo lỗi strict */}
                {(currentQuestion as any).image && (
                  <div style={{ textAlign: "center", marginBottom: 24 }}>
                    <img
                      src={(currentQuestion as any).image}
                      alt="IQ Question"
                      style={{
                        maxWidth: "100%",
                        borderRadius: "8px",
                        boxShadow: "0 4px 12px rgba(0,0,0,0.1)",
                      }}
                    />
                  </div>
                )}

                <div className="option-container">
                  {currentQuestion.options.map((option: any) => {
                    const isSelected =
                      userAnswers[currentQuestion.id] === option.key;
                    return (
                      <div
                        key={option.key}
                        className={`option-card ${
                          isSelected ? "selected" : ""
                        }`}
                        onClick={() =>
                          handleOptionSelect(currentQuestion.id, option.key)
                        }
                      >
                        <div
                          style={{
                            width: 24,
                            height: 24,
                            borderRadius: "50%",
                            border: isSelected
                              ? "6px solid #1890ff"
                              : "2px solid #d9d9d9",
                            marginRight: 16,
                            flexShrink: 0,
                            transition: "all 0.2s",
                          }}
                        />
                        <Text
                          style={{
                            fontSize: "16px",
                            color: isSelected ? "#1890ff" : "inherit",
                          }}
                        >
                          {option.text}
                        </Text>
                        {isSelected && (
                          <CheckCircleFilled
                            style={{
                              color: "#1890ff",
                              position: "absolute",
                              right: 20,
                            }}
                          />
                        )}
                      </div>
                    );
                  })}
                </div>

                <div
                  style={{
                    marginTop: "40px",
                    display: "flex",
                    justifyContent: "space-between",
                    gap: "10px",
                  }}
                >
                  <Button
                    size="large"
                    shape="round"
                    onClick={handlePrevious}
                    disabled={currentQuestionIndex === 0}
                    icon={<LeftOutlined />}
                    style={{ padding: "0 30px", height: "45px" }}
                  >
                    Quay lại
                  </Button>

                  <Button
                    type="primary"
                    size="large"
                    onClick={handleNext}
                    shape="round"
                    style={{ padding: "0 30px", height: "45px" }}
                    disabled={!userAnswers[currentQuestion.id]}
                  >
                    {currentQuestionIndex === questionsData.length - 1
                      ? "Hoàn thành"
                      : "Tiếp tục"}{" "}
                    <RightOutlined />
                  </Button>
                </div>
              </Card>
            ) : (
              <ResultScreen />
            )}
          </div>
        </Content>
        <Footer className="app-footer" style={{ textAlign: "center" }}>
          IQ Test Pro © {new Date().getFullYear()} • Designed for Minds
        </Footer>
      </Layout>
    </ConfigProvider>
  );
};

export default App;
